import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Star, Sparkles, Headphones, Package, Clock, Save, Loader2, X } from 'lucide-react';
import { PriceDisplay } from '@/components/PriceDisplay';
import { durationColor } from '@/lib/badgeColors';

function ProductMiniCard({ product, onRemove }) {
  if (!product) return null;
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
      <div className="w-9 h-9 rounded-lg bg-[#1A9DA5]/10 flex items-center justify-center shrink-0">
        {product.type === 'package' ? <Package className="w-4 h-4 text-[#1A9DA5]" /> : <Headphones className="w-4 h-4 text-[#1A9DA5]" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{product.title}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline" className="text-[10px] h-4">{product.type}</Badge>
          {product.duration_minutes && <span><Clock className="w-3 h-3 inline mr-0.5" />{product.duration_minutes}m</span>}
        </div>
      </div>
      <PriceDisplay product={product} size="sm" />
      {onRemove && (
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onRemove}><X className="w-3.5 h-3.5 text-muted-foreground" /></Button>
      )}
    </div>
  );
}

export default function Featured() {
  const [products, setProducts] = useState([]);
  const [heroId, setHeroId] = useState('');
  const [suggestedIds, setSuggestedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/products?status=published').then(r => setProducts(r.data)),
      api.get('/featured').then(r => {
        setHeroId(r.data.hero_product_id || '');
        setSuggestedIds(r.data.suggested_product_ids || []);
      }),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/featured', { hero_product_id: heroId, suggested_product_ids: suggestedIds });
      toast.success('Featured products saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const addSuggested = (id) => {
    if (!id || suggestedIds.includes(id) || suggestedIds.length >= 3) return;
    setSuggestedIds([...suggestedIds, id]);
  };

  const removeSuggested = (id) => setSuggestedIds(suggestedIds.filter(s => s !== id));

  const heroProduct = products.find(p => p.id === heroId);
  const suggestedProducts = suggestedIds.map(id => products.find(p => p.id === id)).filter(Boolean);
  const availableForSuggested = products.filter(p => p.id !== heroId && !suggestedIds.includes(p.id));

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#1A9DA5]" /></div>;

  return (
    <div className="space-y-6" data-testid="featured-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit']">Featured Products</h1>
          <p className="text-sm text-muted-foreground">Highlight products on the store homepage</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#1A9DA5] hover:bg-[#158D92]" data-testid="save-featured-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Save
        </Button>
      </div>

      {/* Hero Featured Product */}
      <Card className="border-2 border-[#E8735A]/20">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Star className="w-4 h-4 text-[#E8735A]" />
            Hero Featured Product
          </CardTitle>
          <p className="text-xs text-muted-foreground">This product gets a large, prominent spotlight at the top of the store page.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select value={heroId || '__none__'} onValueChange={v => setHeroId(v === '__none__' ? '' : v)}>
            <SelectTrigger data-testid="hero-product-select"><SelectValue placeholder="Select a product to feature..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {products.map(p => (
                <SelectItem key={p.id} value={p.id}>
                  <span className="flex items-center gap-2">
                    {p.type === 'package' ? <Package className="w-3 h-3" /> : <Headphones className="w-3 h-3" />}
                    {p.title}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {heroProduct && <ProductMiniCard product={heroProduct} onRemove={() => setHeroId('')} />}
        </CardContent>
      </Card>

      {/* Suggested Products */}
      <Card className="border-2 border-[#1A9DA5]/20">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#1A9DA5]" />
            Suggested Products
            <Badge variant="outline" className="ml-auto">{suggestedIds.length}/3</Badge>
          </CardTitle>
          <p className="text-xs text-muted-foreground">Up to 3 products displayed in a "Recommended" row on the store page.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {suggestedIds.length < 3 && (
            <Select value="__placeholder__" onValueChange={v => { if (v !== '__placeholder__') addSuggested(v); }}>
              <SelectTrigger data-testid="suggested-product-select"><SelectValue placeholder="Add a suggested product..." /></SelectTrigger>
              <SelectContent>
                {availableForSuggested.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    <span className="flex items-center gap-2">
                      {p.type === 'package' ? <Package className="w-3 h-3" /> : <Headphones className="w-3 h-3" />}
                      {p.title}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {suggestedProducts.length > 0 ? (
            <div className="space-y-2">
              {suggestedProducts.map(p => (
                <ProductMiniCard key={p.id} product={p} onRemove={() => removeSuggested(p.id)} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No suggested products selected yet.</p>
          )}
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader><CardTitle className="text-base">Preview</CardTitle></CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground mb-4">This is how the featured section will appear on the store homepage.</p>
          {!heroProduct && suggestedProducts.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Select products above to see a preview.</p>
          ) : (
            <div className="space-y-4 p-4 rounded-lg bg-muted/50 border">
              {heroProduct && (
                <div className="p-5 rounded-xl bg-gradient-to-r from-[#0C2D3F] to-[#1A4A5A] text-white">
                  <Badge className="bg-[#E8735A] text-white mb-2">Featured</Badge>
                  <h3 className="text-xl font-bold font-['Outfit']">{heroProduct.title}</h3>
                  <p className="text-white/60 text-sm mt-1 line-clamp-2">{heroProduct.short_description}</p>
                  <div className="mt-3"><PriceDisplay product={heroProduct} size="md" /></div>
                </div>
              )}
              {suggestedProducts.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Recommended</p>
                  <div className="grid grid-cols-3 gap-2">
                    {suggestedProducts.map(p => (
                      <div key={p.id} className="p-3 rounded-lg border bg-card text-center">
                        <p className="text-sm font-medium truncate">{p.title}</p>
                        <PriceDisplay product={p} size="sm" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
